import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalPage, LegalSection } from "@/components/LegalPage";
import { ObfuscatedEmail } from "@/components/ObfuscatedEmail";
import { COMPANY } from "@/data/company";
import { apriPreferenzeCookie } from "@/lib/consent";

// Elenco riscritto il 29/10/2026 su quello che il sito salva davvero (src/lib/consent.ts,
// src/store/booking.ts, src/components/PromoPopup.tsx, src/lib/admin/session.ts).
// Se si aggiunge un servizio di terze parti: aggiungerlo qui, nel banner e alzare VERSION in consent.ts.

export const Route = createFileRoute("/cookie")({
  head: () => ({
    meta: [
      { title: "Cookie Policy · La Bottega delle Carni da Marco" },
      { name: "description", content: "Cookie e strumenti simili usati dal sito de La Bottega delle Carni da Marco: quali sono, a cosa servono, quanto durano e come cambiare le tue scelte." },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Cookie Policy · La Bottega delle Carni da Marco" },
      { property: "og:description", content: "Cookie tecnici, statistiche e mappa: cosa usiamo e come gestire le preferenze." },
      { property: "og:url", content: "https://labottegadellecarnidamarco.it/cookie" },
    ],
    links: [{ rel: "canonical", href: "https://labottegadellecarnidamarco.it/cookie" }],
  }),
  component: CookiePage,
});

const link = "text-brand-red hover:underline";

type Riga = { nome: string; chi: string; tipo: string; scopo: string; durata: string };

const TECNICI: Riga[] = [
  { nome: "bottega-consenso-cookie", chi: "Questo sito (localStorage)", tipo: "Tecnico", scopo: "Ricorda le tue scelte sui cookie e la data in cui le hai fatte.", durata: "6 mesi" },
  { nome: "bottega-richieste-v2", chi: "Questo sito (localStorage)", tipo: "Tecnico", scopo: "Ricorda i prodotti che hai messo nella richiesta di disponibilità finché non la invii.", durata: "Finché non la invii o la svuoti" },
  { nome: "lbdc-promo-popup-seen", chi: "Questo sito (sessionStorage)", tipo: "Tecnico", scopo: "Evita di mostrarti di nuovo il riquadro delle offerte durante la stessa visita.", durata: "Fino alla chiusura del browser" },
  { nome: "bottega_admin_session", chi: "Questo sito (localStorage)", tipo: "Tecnico", scopo: "Solo per il personale che entra nell'area riservata: mantiene l'accesso.", durata: "90 giorni" },
];

const FACOLTATIVI: Riga[] = [
  { nome: "_ga, _ga_SH5QJM52S0", chi: "Google (Google Analytics 4)", tipo: "Statistiche, solo con consenso", scopo: "Contano le visite e le pagine viste e distinguono un visitatore dall'altro.", durata: "Fino a 2 anni" },
  { nome: "NID, AEC e altri cookie di Google", chi: "Google (Google Maps)", tipo: "Terze parti, solo con consenso", scopo: "Fanno funzionare la mappa interattiva della pagina Contatti; Google li usa anche per le sue finalità.", durata: "Fino a 6 mesi (vedi l'informativa di Google)" },
];

function Tabella({ righe }: { righe: Riga[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left border-b border-brand-charcoal/15">
            <th className="py-2 pr-4 font-display">Nome</th>
            <th className="py-2 pr-4 font-display">Di chi</th>
            <th className="py-2 pr-4 font-display">Tipo</th>
            <th className="py-2 pr-4 font-display">A cosa serve</th>
            <th className="py-2 font-display">Durata</th>
          </tr>
        </thead>
        <tbody className="text-brand-charcoal/85 align-top">
          {righe.map((r) => (
            <tr key={r.nome} className="border-b border-brand-charcoal/10">
              <td className="py-2 pr-4 font-mono text-xs break-all">{r.nome}</td>
              <td className="py-2 pr-4">{r.chi}</td>
              <td className="py-2 pr-4">{r.tipo}</td>
              <td className="py-2 pr-4">{r.scopo}</td>
              <td className="py-2">{r.durata}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CookiePage() {
  return (
    <LegalPage
      eyebrow="Cookie policy"
      title="Cookie Policy"
      intro="I cookie che usiamo sono pochi, essenziali e dichiarati."
      updated="29 ottobre 2026"
    >
      <LegalSection title="1. Cosa sono">
        <p>
          I cookie sono piccoli file che un sito salva nel tuo browser. Strumenti simili sono il
          localStorage e il sessionStorage, che conservano informazioni allo stesso modo. Possono
          essere del sito che visiti (prima parte) o di altre aziende (terze parti).
        </p>
      </LegalSection>

      <LegalSection title="2. Tecnici: sempre attivi">
        <p>
          Servono a far funzionare il sito e a ricordare le scelte che fai tu. Non servono a profilarti e
          per legge non richiedono il consenso.
        </p>
        <Tabella righe={TECNICI} />
      </LegalSection>

      <LegalSection title="3. Facoltativi: solo se accetti">
        <p>
          Al primo accesso un banner ti chiede se accettarli: puoi scegliere «Accetta», «Rifiuta» o
          decidere categoria per categoria con «Personalizza». Chiudere il banner con la X vale come
          rifiuto e scorrere la pagina non vale come consenso. Finché non accetti, Google Analytics non
          viene caricato e al posto della mappa di Google vedi un'immagine della zona salvata sul nostro
          sito (© OpenStreetMap contributors).
        </p>
        <Tabella righe={FACOLTATIVI} />
        <p>
          Google tratta questi dati secondo la{" "}
          <a className={link} href="https://policies.google.com/privacy?hl=it" target="_blank" rel="noopener noreferrer">sua informativa</a>{" "}
          (come usa i dati dei siti partner:{" "}
          <a className={link} href="https://policies.google.com/technologies/partner-sites?hl=it" target="_blank" rel="noopener noreferrer">policies.google.com</a>).
          Non usiamo cookie pubblicitari né di profilazione.
        </p>
      </LegalSection>

      <LegalSection title="4. Come cambiare le tue scelte">
        <p>
          Ricordiamo la tua scelta per 6 mesi, poi te la chiediamo di nuovo. Puoi cambiarla quando vuoi da{" "}
          <button type="button" onClick={apriPreferenzeCookie} className={link}>Preferenze cookie</button>,
          il link che trovi anche in fondo a ogni pagina. Se revochi il consenso alle statistiche,
          cancelliamo dal browser i cookie di Google Analytics.
        </p>
        <p>
          Puoi anche cancellare o bloccare i cookie dalle impostazioni del browser: senza i tecnici alcune
          parti del sito, come la richiesta di disponibilità, potrebbero non funzionare.
        </p>
        <p>
          Link esterni (WhatsApp, Facebook, Instagram, recensioni su Google) si aprono solo se li scegli tu e
          da lì valgono le informative di quei siti.
        </p>
      </LegalSection>

      <LegalSection title="5. Titolare e contatti">
        <p>
          Titolare del trattamento è <strong>{COMPANY.ragioneSociale}</strong>, sede legale in{" "}
          {COMPANY.sedeLegale}, P. IVA {COMPANY.piva}. Per qualsiasi informazione scrivi a{" "}
          <ObfuscatedEmail className={link} />. Come trattiamo i dati personali è spiegato nella{" "}
          <Link to="/privacy" className={link}>Privacy Policy</Link>.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
